import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  PlatformApiError,
  type ApplicationDetail,
  type ApplicationPage,
  type Viewer,
} from '@sfc/sdk';
import { platform } from '../api/client';
import { APPLICATION_STAGES, APPLICATION_STATUSES, statusLabel } from '../features/status';
import { DetailPane, DetailSection, Split } from '../ui/DetailPane';
import { Empty } from '../ui/Empty';
import { dateTime, initials, relativeTime } from '../ui/format';
import { Icon } from '../ui/Icon';
import { SearchBox } from '../ui/SearchBox';
import { StatStrip } from '../ui/StatStrip';
import { ReviewControls } from './ReviewControls';

export function ApplicationsPanel({ viewer }: { viewer: Viewer }): React.JSX.Element {
  const organizationId = viewer.organization.id;
  const [page, setPage] = useState<ApplicationPage>();
  const [detail, setDetail] = useState<ApplicationDetail>();
  const [problem, setProblem] = useState<string>();
  const [busy, setBusy] = useState(false);
  const [status, setStatus] = useState('');
  const [query, setQuery] = useState('');
  const detailRequest = useRef<AbortController | null>(null);

  const load = useCallback(
    async (cursor?: string, statusFilter = '', signal?: AbortSignal) => {
      setBusy(true);
      setProblem(undefined);
      try {
        setPage(
          await platform.applications.list(
            organizationId,
            { cursor, status: statusFilter || undefined },
            signal,
          ),
        );
      } catch (error) {
        if (!signal?.aborted) setProblem(describe(error));
      } finally {
        if (!signal?.aborted) setBusy(false);
      }
    },
    [organizationId],
  );
  useEffect(() => {
    const controller = new AbortController();
    setPage(undefined);
    setDetail(undefined);
    void load(undefined, '', controller.signal);
    return () => {
      controller.abort();
      detailRequest.current?.abort();
    };
  }, [load]);

  async function open(id: string) {
    detailRequest.current?.abort();
    const controller = new AbortController();
    detailRequest.current = controller;
    setDetail(undefined);
    setProblem(undefined);
    try {
      setDetail(await platform.applications.detail(organizationId, id, controller.signal));
    } catch (error) {
      if (!controller.signal.aborted) setProblem(describe(error));
    }
  }
  async function download() {
    // An imported record may be waiting for its resume, or be known to have
    // none. There is nothing to fetch, and the button is not shown.
    if (!detail?.resume) return;
    setProblem(undefined);
    try {
      const blob = await platform.documents.download(organizationId, detail.resume.id);
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = detail.resume.fileName;
      link.click();
      URL.revokeObjectURL(url);
    } catch (error) {
      setProblem(describe(error));
    }
  }

  /**
   * The inbox, not the page of it on screen.
   *
   * These used to count the rows that happened to be loaded, which is the page
   * size until the last page: an inbox of thirty-six read as twenty-five, and
   * anybody who did not think to page forward never saw the rest. The API sends
   * the real counts alongside the page now.
   */
  const stats = useMemo(() => {
    const counts = page?.counts;
    const inStage = (stage: string) => counts?.byStatus?.[stage] ?? 0;
    return [
      { label: 'Applications', value: counts?.total ?? 0 },
      { label: 'In review', value: inStage('in_review') },
      { label: 'Shortlisted', value: inStage('shortlisted') },
      { label: 'Offered', value: inStage('offered') },
      { label: 'On this page', value: page?.items.length ?? 0 },
    ];
  }, [page]);

  const needle = query.trim().toLowerCase();
  const shown = (page?.items ?? []).filter(
    (entry) =>
      !needle ||
      `${entry.firstName} ${entry.lastName} ${entry.email} ${entry.jobTitle}`.toLowerCase().includes(needle),
  );

  return (
    <section className="card" aria-labelledby="section-heading">
      <div className="toolbar">
        <SearchBox label="Search applications" placeholder="Search this page by name, email or role" value={query} onChange={setQuery} />
        <select
          className="select"
          aria-label="Filter by status"
          data-testid="status-filter"
          value={status}
          onChange={(event) => {
            setStatus(event.target.value);
            void load(undefined, event.target.value);
          }}
        >
          <option value="">All statuses</option>
          {APPLICATION_STATUSES.map((value) => (
            <option key={value} value={value}>
              {statusLabel(value)}
            </option>
          ))}
        </select>
        <span className="toolbar__spacer" />
        <button className="button" disabled={busy} onClick={() => void load(undefined, status)}>
          <Icon name="refresh" size={16} />
          Refresh
        </button>
      </div>

      <StatStrip stats={stats} testId="application-stats" />

      {problem ? (
        <p className="notice notice--problem" role="alert">
          {problem}
        </p>
      ) : null}
      {busy && !page ? (
        <p className="loading" role="status">
          Loading applications…
        </p>
      ) : null}

      <Split open={Boolean(detail)}>
        <div className="split__list">
          {page?.items.length === 0 ? (
            <Empty
              icon="applications"
              title="No applications yet"
              hint="Every application sent from the careers page lands here, newest first."
            />
          ) : null}
          {page && page.items.length > 0 && shown.length === 0 ? (
            <Empty icon="search" title="Nothing on this page matches" hint="Clear the search, or load older applications." />
          ) : null}

          <ul className="ledger" aria-label="Application inbox" data-testid="applications">
            {shown.map((entry) => (
              <li
                className={`ledger__row${detail?.id === entry.id ? ' ledger__row--selected' : ''}`}
                key={entry.id}
                onClick={() => void open(entry.id)}
              >
                <span className="avatar" aria-hidden="true">
                  {initials(`${entry.firstName} ${entry.lastName}`)}
                </span>
                <div className="ledger__id">
                  <span className="row__title">
                    {entry.firstName} {entry.lastName}
                  </span>
                  <span className="row__sub">{entry.jobTitle}</span>
                </div>
                <StatusRail status={entry.status} />
                <div className="ledger__meta">
                  <time className="row__sub" dateTime={entry.createdAt} title={dateTime(entry.createdAt)}>
                    {relativeTime(entry.createdAt)}
                  </time>
                  {entry.source === 'import' ? (
                    <span className="row__sub" data-testid={`provenance-${entry.id}`}>
                      Imported{entry.resumeState === 'attached' ? '' : `, resume ${entry.resumeState}`}
                    </span>
                  ) : null}
                </div>
                <button
                  className="button button--small"
                  aria-label={`Read application from ${entry.firstName} ${entry.lastName}`}
                  onClick={(event) => {
                    event.stopPropagation();
                    void open(entry.id);
                  }}
                >
                  Read
                </button>
              </li>
            ))}
          </ul>

          {page?.nextCursor ? (
            <button className="button pager" disabled={busy} onClick={() => void load(page.nextCursor!, status)}>
              Older applications
            </button>
          ) : null}
        </div>

        {detail ? (
          <DetailPane
            label="Application details"
            recordKey={detail.id}
            title={`${detail.firstName} ${detail.lastName}`}
            subtitle={
              <>
                Applied for {detail.jobTitle},{' '}
                <time dateTime={detail.createdAt} title={dateTime(detail.createdAt)}>
                  {relativeTime(detail.createdAt).toLowerCase()}
                </time>
              </>
            }
            closeLabel="Close details"
            onClose={() => setDetail(undefined)}
          >
            <dl className="facts">
              <div>
                <dt>Email</dt>
                <dd>
                  <a href={`mailto:${detail.email}`}>{detail.email}</a>
                </dd>
              </div>
              <div>
                <dt>Phone</dt>
                <dd>{detail.phone}</dd>
              </div>
              <div>
                <dt>LinkedIn</dt>
                <dd>
                  {detail.linkedin ? (
                    <a href={detail.linkedin} target="_blank" rel="noreferrer noopener">
                      {detail.linkedin.replace(/^https?:\/\/(www\.)?/, '')}
                    </a>
                  ) : (
                    'Not provided'
                  )}
                </dd>
              </div>
              <div>
                <dt>Received</dt>
                <dd>
                  <time dateTime={detail.createdAt}>{dateTime(detail.createdAt)}</time>
                </dd>
              </div>
            </dl>

            <DetailSection title="Cover letter">
              <p className="letter application-letter">{detail.coverLetter || 'Not provided'}</p>
            </DetailSection>

            <DetailSection title="Resume">
              {detail.resume ? (
                <div className="file">
                  <span className="file__icon">
                    <Icon name="documents" />
                  </span>
                  <span className="file__name">
                    <span className="row__title">{detail.resume.fileName}</span>
                    <span className="row__sub">{Math.ceil(detail.resume.byteSize / 1024)} KB</span>
                  </span>
                  <button className="button button--small" onClick={() => void download()}>
                    <Icon name="download" size={16} />
                    Download resume
                  </button>
                </div>
              ) : (
                // Said plainly rather than left as an absence. A record whose
                // resume has not arrived must not read as one that never had a
                // resume, and neither must read as a complete application.
                //
                // The wording does not claim which of the two it is, because this
                // screen cannot tell: `missing` covers a sheet that never held a
                // link and a file that could not be copied. The reconciliation
                // report is where the reason is.
                <p className="notice" data-testid="resume-state">
                  {detail.resumeState === 'pending'
                    ? 'The resume for this application has not been copied across yet.'
                    : 'This application has no resume here. Either it was imported without one, or the file it pointed at could not be copied across.'}
                </p>
              )}
            </DetailSection>

            <ReviewControls
              viewer={viewer}
              detail={detail}
              onChanged={() => {
                void open(detail.id);
                void load(undefined, status);
              }}
              onErased={() => {
                setDetail(undefined);
                void load(undefined, status);
              }}
            />
          </DetailPane>
        ) : null}
      </Split>
    </section>
  );
}

/**
 * Where an application is on the path.
 *
 * The stages are a real sequence, so they are numbered and filled up to where
 * the candidate has reached. A rejection or a withdrawal is not a stage, so it
 * is a single mark rather than a position on the line.
 */
function StatusRail({ status }: { status: string }): React.JSX.Element {
  const index = (APPLICATION_STAGES as readonly string[]).indexOf(status);
  const ended = index === -1;

  if (ended) {
    return (
      <span className={`rail rail--ended rail--${status === 'rejected' ? 'down' : 'muted'}`}>
        <span className="rail__mark" aria-hidden="true" />
        {statusLabel(status)}
      </span>
    );
  }

  return (
    <ol className="rail" aria-label="Progress">
      {APPLICATION_STAGES.map((stage, position) => (
        <li
          key={stage}
          className={`rail__step${position <= index ? ' rail__step--done' : ''}${
            position === index ? ' rail__step--current' : ''
          }`}
        >
          <span className="rail__tick" aria-hidden="true" />
          <span className="sr-only">{statusLabel(stage)}</span>
        </li>
      ))}
      <li className="rail__now">{statusLabel(status)}</li>
    </ol>
  );
}

function describe(error: unknown): string {
  return error instanceof PlatformApiError
    ? error.message
    : 'The applications could not be loaded. Please try again.';
}
